import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { TextInput } from "../../../../form/textInput";
import { Textarea } from "../../../../form/textarea";
import { updateNodeValue } from "../../../../../services/method";
import { webURL } from "../../../../../services/API";
import { operationType, selectDataType } from "../../../../../services/types";
import { AuthenticateSelect } from "../../../../auth/select";
import { Select } from "../../../../form/select";
import { Main, Styled } from "./style";
import { Switch } from "../../../../form/switch";
import { Checkboxs } from "../../../../form/checkboxs";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type OdooProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

let reqStatus = { columns: false, leads: false, notes: false, contacts: false };

// Resource options
const resourceOptions: selectDataType[] = [
   { id: 1, name: 'Lead', refId: 'lead', isActive: true },
   { id: 2, name: 'Note', refId: 'note' },
   { id: 3, name: 'Contact', refId: 'contact' },
];

// Request options per resource
const leadRequests: selectDataType[] = [
   { id: 1, name: 'Fetch Attachments', refId: 'fetch_attachments', isActive: true },
   { id: 2, name: 'Fetch Leads', refId: 'fetch_leads' },
   { id: 3, name: 'Get Lead Details', refId: 'lead_details' },
   { id: 4, name: 'New Lead', refId: 'new_lead' },
   { id: 5, name: 'Update Lead', refId: 'update_lead' },
   { id: 6, name: 'Delete Lead', refId: 'delete_lead' },
   { id: 7, name: 'Let AI Agent Decide', refId: 'agent_decide' },
];

const noteRequests: selectDataType[] = [
   { id: 1, name: 'Get All Notes', refId: 'get_all_notes', isActive: true },
   { id: 2, name: 'Get Note', refId: 'get_note' },
   { id: 3, name: 'Create Note', refId: 'create_note' },
   { id: 4, name: 'Update Note', refId: 'update_note' },
   { id: 5, name: 'Delete Note', refId: 'delete_note' },
   { id: 7, name: 'Let AI Agent Decide', refId: 'agent_decide' },
];

const contactRequests: selectDataType[] = [
   { id: 1, name: 'Get All Contacts', refId: 'get_all_contacts', isActive: true },
   { id: 2, name: 'Get Contact', refId: 'get_contact' },
   { id: 3, name: 'Create Contact', refId: 'create_contact' },
   { id: 4, name: 'Update Contact', refId: 'update_contact' },
   { id: 5, name: 'Delete Contact', refId: 'delete_contact' },
   { id: 7, name: 'Let AI Agent Decide', refId: 'agent_decide' },
];

export function Odoo({ id, data, setChildMinimize, setChildSettings }: OdooProps) {
   const [resources, setResources] = React.useState<selectDataType[]>(resourceOptions);
   const [requests, setRequests] = React.useState<selectDataType[]>(leadRequests);
   const { setNodes } = useReactFlow();
   const conn = data.value?.connection;
   const getOdooCredentials = () => conn ? { url: conn.url, db: conn.db, username: conn.username, password: conn.password } : null;
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);

   // Get requests based on current resource
   const getRequestsForResource = (resource: string) => {
      switch (resource) {
         case 'note': return noteRequests;
         case 'contact': return contactRequests;
         default: return leadRequests;
      }
   };

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // Initialize resource and request
   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, { resource: 'lead', request: 'fetch_attachments' });
      } else {
         const currentResource = data.value.resource || 'lead';
         setResources(resources => resources.map(r => ({ ...r, isActive: r.refId === currentResource })));
         const reqs = getRequestsForResource(currentResource);
         setRequests(reqs.map(r => ({ ...r, isActive: r.refId === data.value.request })));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // Fetch leads when columns change (for Lead and Note resource)
   React.useEffect(() => {
      if (reqStatus.columns && data.value?.columns?.length && (data.value?.resource === 'lead' || data.value?.resource === 'note')) {
         const fetchData = async () => {
            reqStatus.columns = false;
            const columnId = data.value.columns.find((item: any) => item.isActive)?.id;
            if (!columnId) return;
            const leads = (await axios.post(`/crm/odoo/leads/${columnId}`, getOdooCredentials())).data;
            updateNodeValue(id, setNodes, { leads: leads.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
         }
         fetchData();
      } // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.columns]);

   // Handle connection authentication
   const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, { connection: connectionData });
         const creds = { url: connectionData.url, db: connectionData.db, username: connectionData.username, password: connectionData.password };
         try {
            // Fetch columns for Lead resource
            const response = await axios.post(`/crm/odoo/pipeline/columns`, creds);
            const columns = response.data;
            reqStatus.columns = true;
            updateNodeValue(id, setNodes, { columns: columns.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
         } catch (err: any) {
            console.error('Failed to fetch Odoo columns:', err.response?.data || err.message || err);
         }
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { connection: null, columns: [], leads: [], notes: [], contacts: [] });
      }
   }

   // Handle input changes
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string, isInfo?: boolean) => {
      if (keyRef) {
         if (isInfo) setNodes(nodes => nodes.map(node => {
            if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, info: { ...node.data.value.info, [keyRef]: e.target.value } } } }
            else return node;
         }));
         else updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
      }
   }

   // Handle resource selection
   const handleSelectResource = async (selectId: number) => {
      const refId = resources.find(r => r.id === selectId)?.refId || 'lead';
      setResources(resources => resources.map(r => ({ ...r, isActive: r.id === selectId })));

      // Update requests based on selected resource
      const newRequests = getRequestsForResource(refId);
      setRequests(newRequests.map((r, i) => ({ ...r, isActive: i === 0 })));

      // Reset data when resource changes
      updateNodeValue(id, setNodes, {
         resource: refId,
         request: newRequests[0].refId,
         notes: [],
         contacts: []
      });

      // Fetch notes or contacts if needed
      if (refId === 'note' && conn) {
         try {
            const notes = (await axios.post(`/crm/odoo/notes`, getOdooCredentials())).data;
            updateNodeValue(id, setNodes, { notes: notes.map((item: any, index: number) => ({ id: item.id, name: item.name || `Note ${item.id}`, isActive: index === 0 })) });
         } catch (err) {
            console.error('Failed to fetch notes:', err);
         }
      }
      // Fetch contacts if needed
      if (refId === 'contact' && conn) {
         try {
            // Use webURL to call Node.js backend directly
            const contacts = (await axios.post(`${webURL}/crm/odoo/contacts`, getOdooCredentials())).data;
            updateNodeValue(id, setNodes, { contacts: contacts.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
         } catch (err) {
            console.error('Failed to fetch contacts:', err);
         }
      }
   };

   // Helper function to refresh contacts list
   const refreshContacts = async () => {
      if (!conn) return;
      try {
         const contacts = (await axios.post(`${webURL}/crm/odoo/contacts`, getOdooCredentials())).data;
         updateNodeValue(id, setNodes, { contacts: contacts.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
      } catch (err) {
         console.error('Failed to refresh contacts:', err);
      }
   };

   // Handle request and other selections
   const handleSelectRequests = async (action: 'request' | 'columns' | 'leads' | 'notes' | 'contacts', selectId: number) => {
      if (action === 'request') {
         const refId = requests.find(obj => obj.id === selectId)?.refId;
         setRequests((requests) => requests.map((request) => {
            if (request.id === selectId) return { ...request, isActive: true };
            else return { ...request, isActive: false };
         }));
         setNodes(nodes => nodes.map(node => {
            if (node.id === id) {
               return { ...node, data: { ...node.data, value: { ...node.data.value, request: refId } } }
            } else return node;
         }));

         // Auto-refresh contacts when switching to update/delete contact requests
         if (['update_contact', 'delete_contact', 'get_contact'].includes(refId || '')) {
            await refreshContacts();
         }
      } else if (action === 'columns' || action === 'leads' || action === 'notes' || action === 'contacts') {
         const arr = data.value[action]?.map((item: any) => ({ ...item, isActive: item.id === selectId })) || [];
         if (action === 'columns') reqStatus.columns = true;
         updateNodeValue(id, setNodes, { [action]: arr });

         // Fetch notes when a lead is selected in Note resource mode
         if (action === 'leads' && data.value?.resource === 'note' && conn) {
            try {
               // Use webURL to call Node.js backend directly (not through Python API proxy)
               const notes = (await axios.post(`${webURL}/crm/odoo/notes/${selectId}`, getOdooCredentials())).data;
               // Strip HTML tags from body for cleaner display
               const stripHtml = (html: string) => html?.replace(/<[^>]*>/g, '') || '';
               updateNodeValue(id, setNodes, { notes: notes.map((item: any, index: number) => ({ id: item.id, name: stripHtml(item.body)?.substring(0, 30) || `Note ${item.id}`, isActive: index === 0 })) });
            } catch (err) {
               console.error('Failed to fetch notes:', err);
               updateNodeValue(id, setNodes, { notes: [] });
            }
         }
      }
   }

   // Handle button actions
   const handleButtons = async (type: 'attachments' | 'download', value?: any) => {
      try {
         if (type === 'attachments') {
            updateNodeValue(id, setNodes, { isLoading: true });
            const columnId = data.value.columns.find((item: any) => item.isActive).id;
            const response = (await axios.post(`${webURL}/crm/odoo/attachments/${columnId}`, getOdooCredentials())).data;
            updateNodeValue(id, setNodes, {
               isLoading: false, attachments: response.attachments.map((file: any) => ({ id: file.id, name: file.name, type: file.mimetype }))
            });
         } else if (type === 'download') {
            const filter = data.value.attachments.filter((file: any) => file.isActive && !file.url).map((file: any) => file.id);
            if (filter.length) {
               updateNodeValue(id, setNodes, { isLoading: true });
               const files: { id: number, url: string }[] = [];
               for (const fileId of filter) {
                  const result = (await axios.post(`${webURL}/crm/odoo/attachments/${fileId}/download`, getOdooCredentials())).data;
                  files.push({ id: fileId, url: result.url });
               }
               const reMap = data.value.attachments.map((file: any) => ({ ...file, url: file.url || files.find((f) => f.id === file.id)?.url || '' }))
               updateNodeValue(id, setNodes, { isLoading: false, attachments: reMap });
               files.forEach(file => window.open(file.url, '_blank'));
            } else {
               const activeFiles = data.value.attachments.filter((file: any) => file.isActive && file.url);
               activeFiles.forEach((file: any) => window.open(file.url, '_blank'));
            }
         }
      } catch {
         updateNodeValue(id, setNodes, { isLoading: false });
      }
   }

   // Computed values
   const currentResource = data.value?.resource || 'lead';
   const currentRequest = data.value?.request || '';
   const attachmentsLengthActive = currentRequest === 'fetch_attachments' ? data.value?.attachments?.filter((item: any) => item.isActive)?.length : 0;
   const isWarning = !data.value?.connection;

   // Check if we need to show specific selectors
   const showColumnId = currentResource === 'lead' || currentResource === 'note';
   const showLeadId = (currentResource === 'lead' && ['update_lead', 'lead_details', 'delete_lead'].includes(currentRequest)) ||
      (currentResource === 'note' && ['create_note', 'update_note', 'delete_note', 'get_note'].includes(currentRequest));
   const showNoteId = currentResource === 'note' && ['get_note', 'update_note', 'delete_note'].includes(currentRequest);
   const showContactId = currentResource === 'contact' && ['get_contact', 'update_contact', 'delete_contact'].includes(currentRequest);

   // Check if we need to show form fields
   const showLeadFields = currentResource === 'lead' && ['new_lead', 'update_lead'].includes(currentRequest);
   const showNoteFields = currentResource === 'note' && ['create_note', 'update_note'].includes(currentRequest);
   const showContactFields = currentResource === 'contact' && ['create_contact', 'update_contact'].includes(currentRequest);

   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ width: '420px' }}
         ><Styled>
               <AuthenticateSelect type="odoo" defaultId={data.value?.connection?.id} handleAuthenticate={handleAuthenticate} background={data.background} />

               {/* Action buttons for fetch_attachments */}
               <div className="action">
                  {data.value?.connection && currentRequest === 'fetch_attachments' ? <>
                     <button className="btn1 withBorder" onClick={_ => handleButtons('attachments')} disabled={data.value?.isLoading}>Fetch</button>
                     {attachmentsLengthActive ? <button className="btn1 withBorder" onClick={_ => handleButtons('download')} disabled={data.value?.isLoading}>Download</button> : null}
                  </> : null}
               </div>

               {/* Resource Select */}
               <Select title="Resource" data={resources} handleSelectItem={(id) => handleSelectResource(id)} isSmall={true} />

               {/* Request Select */}
               <Select title="Request" data={requests} handleSelectItem={(id) => handleSelectRequests('request', id)} isSmall={true} />

               {/* Column Id (Lead only) */}
               {showColumnId && <Select title="Column Id" data={data.value?.columns} handleSelectItem={(id) => handleSelectRequests('columns', id)} isSmall={true} />}

               {/* Lead Id */}
               {showLeadId && <Select title="Lead Id" data={data.value?.leads} handleSelectItem={(id) => handleSelectRequests('leads', id)} isSmall={true} />}

               {/* Note Id */}
               {showNoteId && <Select title="Note Id" data={data.value?.notes} handleSelectItem={(id) => handleSelectRequests('notes', id)} isSmall={true} />}

               {/* Contact Id */}
               {showContactId && <Select title="Contact Id" data={data.value?.contacts} handleSelectItem={(id) => handleSelectRequests('contacts', id)} isSmall={true} />}

               {/* Lead form fields */}
               {showLeadFields && <div className="scroll nowheel">
                  <TextInput keyRef="name" title="Name" placeholder="lead_name" value={data.value?.info?.name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="expected_revenue" title="Revenue" placeholder="expected_revenue" value={data.value?.info?.expected_revenue} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="probability" title="Probability" placeholder="probability" value={data.value?.info?.probability} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="customer" title="Customer" placeholder="customer" value={data.value?.info?.customer} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="email" title="Email" placeholder="email" value={data.value?.info?.email} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="phone" title="Phone" placeholder="phone" value={data.value?.info?.phone} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="salesperson" title="Salesperson" placeholder="salesperson" value={data.value?.info?.salesperson} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="expected_closing" title="Closing" placeholder="expected_closing" value={data.value?.info?.expected_closing} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="company_name" title="Company" placeholder="company_name" value={data.value?.info?.company_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="address" title="Address" placeholder="address" value={data.value?.info?.address} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="contact_name" title="Contact" placeholder="contact_name" value={data.value?.info?.contact_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="job_position" title="Job" placeholder="job_position" value={data.value?.info?.job_position} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="website" title="Website" placeholder="website" value={data.value?.info?.website} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="campaign" title="Campaign" placeholder="campaign" value={data.value?.info?.campaign} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="medium" title="Medium" placeholder="medium" value={data.value?.info?.medium} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="sales_team" title="Sales" placeholder="sales_team" value={data.value?.info?.sales_team} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
               </div>}

               {/* Note form fields */}
               {showNoteFields && <div style={{ marginTop: '15px', paddingTop: '15px', overflow: 'visible' }}>
                  <Textarea keyRef="body" title="Body" placeholder="Note content..." value={data.value?.info?.body} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
               </div>}

               {/* Contact form fields */}
               {showContactFields && <div className="scroll nowheel">
                  <TextInput keyRef="name" title="Name" placeholder="Contact name" value={data.value?.info?.name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="email" title="Email" placeholder="email@example.com" value={data.value?.info?.email} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="phone" title="Phone" placeholder="+1234567890" value={data.value?.info?.phone} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="company" title="Company" placeholder="Company name" value={data.value?.info?.company} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="address" title="Address" placeholder="Full address" value={data.value?.info?.address} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
               </div>}

               {/* Attachments section */}
               {currentRequest === 'fetch_attachments' ? <>
                  {data.value?.attachments?.length ? <div className="filesEdit">
                     <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Attachments ({attachmentsLengthActive}/{data.value.attachments.length})</h3>
                     <div className="scroll nowheel">
                        <Checkboxs
                           data={data.value.attachments.map((file: any) => ({ ...file, isNotActive: !file.url }))}
                           handelCheckbox={(fileId: any, value?: boolean) => {
                              updateNodeValue(id, setNodes, {
                                 attachments: data.value.attachments.map((item: any) => {
                                    if (item.id === fileId) return ({ ...item, isActive: !value })
                                    else return item;
                                 })
                              });
                           }}
                        />
                     </div>
                  </div> : null}
                  <Switch title="Start autopilot (with loop)" className="autopilot" value={!!data.value?.isAutopilot} setValue={() => updateNodeValue(id, setNodes, { isAutopilot: !data.value?.isAutopilot })} />
                  <p>Please select your keywords carefully to avoid downloading incorrect or unintended files!</p>
               </> : null}
            </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}